import React from "react";
import styled, { css } from "styled-components";
import { colors } from "src/theme/colors";
import { line, radii, shadow } from "src/theme/ui";

/**
 * SegmentedControl — the football web app's compact single-select, ported.
 *
 * For a small fixed set of short options that SET a value rather than
 * navigate: a chart type, a row density, a cadence. It is not a tab strip —
 * that is navigation — and not a row of large choice pills.
 *
 * Worth noting how the original draws it, because it is not the common
 * pattern: the segments are separate bordered buttons in a gap, not a tinted
 * track with a white thumb sliding over it. The active one takes an accent
 * tint with an accent edge; the rest are white with a hairline.
 */

export interface SegmentedOption<T extends string = string> {
  value: T;
  label: React.ReactNode;
  disabled?: boolean;
}

export type SegmentedVariant = "buttons" | "track";

/**
 * The rail scrolls; it does not wrap.
 *
 * The group used to be `flex-wrap: wrap`, and a segmented control that wraps
 * is not a segmented control any more: it is a rail with a thumb sliding
 * along it, and the thumb is placed from the selected segment's offset inside
 * ONE line. Squeezed in a table toolbar on a phone, "Summary" and "Detailed"
 * stacked into a two-storey block with the pill around the top half — which
 * is what a reader reported, and it also puts the thumb's geometry a line
 * away from the segment it is meant to be under.
 *
 * So the segments stay on one line and this box scrolls when they genuinely
 * do not fit — the same answer the table's own tab strip already gives, with
 * the scrollbar hidden for the same reason. The 4px of padding pulled back
 * out by an equal negative margin is room for a focused segment's ring: the
 * ring is a 4px spread, the rail's own padding is 3, and a scroll container
 * clips whatever leaves it.
 */
const Scroller = styled.div<{ $variant: SegmentedVariant }>`
  /**
   * Wide enough for its own padding.
   *
   * The rail below adds 4px of padding on each side as room for a focused
   * segment's ring, and pulls it back out with an equal negative margin.
   * Under a border-box sizing rule, a plain max-width of 100% caps the
   * BORDER box, so those 8px came off the space the rail had to sit in: the rail
   * needed 147px, got 139px of content box, and the scroll container clipped
   * the difference. What a reader saw was the right end of the control cut
   * off with the selected segment sliced through, on a row with room to
   * spare either side.
   *
   * Plus 8px, so the content box is the full width of the parent and the
   * padding bleeds outward into the margin it was already given. A genuine
   * overflow, where the parent really is narrower than the segments, still
   * scrolls.
   */
  max-width: ${({ $variant }) =>
    $variant === "track" ? "calc(100% + 8px)" : "100%"};

  /**
   * Only the RAIL scrolls, and only the rail gets the padding.
   *
   * The padding-and-negative-margin pair makes this box 8px wider than the
   * control inside it, which is fine over a rail — the 4px it bleeds is
   * empty and the rail carries its own ground. Over the buttons variant it
   * was not fine: those segments are separate bordered controls sized to
   * their content, so the extra 8px overflowed a header that was exactly as
   * wide as the pair, and the right-hand button lost its border to the clip.
   * The buttons variant has nothing to scroll anyway — a set of bordered
   * buttons can wrap or be given room; it is the sliding thumb that cannot
   * survive a second line.
   */
  ${({ $variant }) =>
    $variant === "track"
      ? `
        padding: 4px;
        margin: -4px;
        overflow-x: auto;
        overflow-y: hidden;
        scrollbar-width: none;

        &::-webkit-scrollbar {
          display: none;
        }
      `
      : ""}
`;

const Group = styled.div<{ $variant: SegmentedVariant }>`
  position: relative;
  display: inline-flex;
  flex: none;
  flex-wrap: nowrap;

  ${({ $variant }) =>
    $variant === "track"
      ? css`
          gap: 2px;
          padding: 3px;
          border-radius: ${radii.lg}px;
          background: ${colors.gray[100]};
        `
      : css`
          gap: 8px;
        `}
`;

const Segment = styled.button<{
  $selected: boolean;
  $size: "sm" | "md";
  $variant: SegmentedVariant;
}>`
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-family: inherit;
  font-weight: 540;
  white-space: nowrap;
  cursor: pointer;
  outline: none;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;

  ${({ $variant, $size }) =>
    $variant === "track"
      ? css`
          border: 0;
          border-radius: ${radii.control}px;
          height: ${$size === "sm" ? 22 : 24}px;
          padding: 0 10px;
          font-size: 12.5px;
        `
      : css`
          border: 1px solid;
          border-radius: 8px;
          ${$size === "sm"
            ? css`
                height: 28px;
                padding: 0 10px;
                font-size: 12px;
              `
            : css`
                height: 30px;
                padding: 0 11px;
                font-size: 12.5px;
              `}
        `}

  ${({ $selected, $variant }) =>
    $variant === "track"
      ? /* A rail with the chosen segment lifted out of it, the way the table's
           own tabs are drawn — so a setting sitting under those tabs reads as
           the same kind of switch rather than as two loose buttons. */
        $selected
        ? css`
            /* No background of its own: the thumb behind it carries the
               white and the shadow, so the selection MOVES between segments
               rather than blinking out of one and into the next. */
            background: transparent;
            color: ${colors.gray[900]};
            font-weight: 600;
          `
        : css`
            background: transparent;
            color: ${colors.gray[600]};

            &:hover {
              color: ${colors.gray[800]};
            }
          `
      : $selected
      ? css`
          border-color: ${colors.primary[100]};
          background: ${colors.primary[10]};
          color: ${colors.primary[700]};
        `
      : css`
          border-color: ${line.hairline};
          background: ${colors.white};
          color: ${colors.gray[700]};

          &:hover {
            border-color: ${colors.gray[400]};
            color: ${colors.gray[900]};
          }
        `}

  &:focus-visible {
    box-shadow: ${shadow.focusRing};
  }

  &:disabled {
    pointer-events: none;
    opacity: 0.5;
  }

  svg {
    width: 13px;
    height: 13px;
  }
`;

export interface SegmentedControlProps<T extends string = string> {
  options: SegmentedOption<T>[];
  value: T;
  onChange: (next: T) => void;
  /** `sm` is the 28px in-row control; `md` (default) the 30px in-card one. */
  size?: "sm" | "md";
  /**
   * `buttons` (default) is football's own shape — separate bordered pills.
   * `track` is the tinted rail with the chosen segment lifted out of it, for
   * a setting that sits beside other chrome and should read as one control.
   */
  variant?: SegmentedVariant;
  className?: string;
  "aria-label"?: string;
}

/**
 * The lifted segment, drawn once and moved.
 *
 * It travels on a transform rather than on `left`, so the move is composited
 * and does not lay the row out again on every frame. It is deaf to the
 * pointer, so it can never take a click meant for a segment underneath it.
 *
 * Held still until it has been measured: without that it would slide in from
 * the left edge on first paint, which is the control announcing itself rather
 * than responding to anything.
 */
const Thumb = styled.span<{ $ready: boolean }>`
  position: absolute;
  top: 3px;
  left: 0;
  z-index: 0;
  border-radius: ${radii.md}px;
  background: ${colors.white};
  box-shadow: ${shadow.thumb};
  pointer-events: none;
  transition: ${({ $ready }) =>
    $ready
      ? "transform 0.28s cubic-bezier(0.22, 1, 0.36, 1), width 0.28s cubic-bezier(0.22, 1, 0.36, 1)"
      : "none"};

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

function SegmentedControl<T extends string = string>({
  options,
  value,
  onChange,
  size = "md",
  variant = "buttons",
  className,
  "aria-label": ariaLabel,
}: SegmentedControlProps<T>) {
  const groupRef = React.useRef<HTMLDivElement>(null);
  const [thumb, setThumb] = React.useState<{
    x: number;
    width: number;
    height: number;
    ready: boolean;
  }>({ x: 0, width: 0, height: 0, ready: false });

  /* Measured from the selected segment itself, so the thumb fits labels of
     any length and follows them when the type reflows. Layout effect, not a
     plain one: it must land in the same frame the selection changes, or the
     thumb is a frame behind the label. */
  React.useLayoutEffect(() => {
    if (variant !== "track") return;
    const group = groupRef.current;
    if (!group) return;

    const place = () => {
      const selected = group.querySelector<HTMLElement>(
        '[aria-checked="true"]',
      );
      if (!selected) return;
      setThumb((prev) => ({
        x: selected.offsetLeft,
        width: selected.offsetWidth,
        height: selected.offsetHeight,
        /* Ready only once it has been somewhere: the first placement jumps,
           every later one slides. */
        ready: prev.width > 0,
      }));
    };

    place();

    /* A resize changes where the segments are — a reflow, a font swap, the
       card getting narrower — and the thumb has to go with them. */
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(place);
    observer.observe(group);
    return () => observer.disconnect();
  }, [value, variant, options, size]);

  return (
    <Scroller className={className} $variant={variant}>
      <Group
        ref={groupRef}
        role="radiogroup"
        aria-label={ariaLabel}
        $variant={variant}
      >
        {variant === "track" && thumb.width > 0 && (
          <Thumb
            aria-hidden="true"
            $ready={thumb.ready}
            style={{
              transform: `translateX(${thumb.x}px)`,
              width: thumb.width,
              height: thumb.height,
            }}
          />
        )}
        {options.map(({ value: v, label, disabled }) => (
          <Segment
            key={v}
            type="button"
            role="radio"
            aria-checked={v === value}
            disabled={disabled}
            $selected={v === value}
            $size={size}
            $variant={variant}
            onClick={() => onChange(v)}
          >
            {label}
          </Segment>
        ))}
      </Group>
    </Scroller>
  );
}

export default SegmentedControl;
